import { Box, Typography, Grid, Button, Card, CardContent, Avatar, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, FormControlLabel, Checkbox, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useState } from 'react';
import MessageIcon from '@mui/icons-material/Message';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import { SectionCard } from '../../components/common';
import patientHomeData from '../../mocks/patientHome.json';

interface CareProvider {
  id: string;
  name: string;
  role: string;
  credentials: string;
  organization: string;
  email: string;
  phone: string;
  isPrimary: boolean;
  specialties: string[];
  availableForMessages: boolean;
  responseTime: string;
  addedAt: string;
}

interface FamilyMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  relationship: string;
  isLegalGuardian: boolean;
  canViewRecords: boolean;
  addedAt: string;
}

const relationshipOptions = [
  { value: 'SPOUSE', label: 'Spouse' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'CHILD', label: 'Child' },
  { value: 'SON', label: 'Son' },
  { value: 'DAUGHTER', label: 'Daughter' },
  { value: 'SIBLING', label: 'Sibling' },
  { value: 'GUARDIAN', label: 'Guardian' },
  { value: 'OTHER', label: 'Other' },
];

export const CareNetworkPage = () => {
  const { careTeam: initialCareTeam, familyMembers: initialFamilyMembers } = patientHomeData;

  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>(initialFamilyMembers as FamilyMember[]);
  const [careTeam, setCareTeam] = useState<CareProvider[]>(initialCareTeam as CareProvider[]);

  const [openAddFamily, setOpenAddFamily] = useState(false);
  const [newMember, setNewMember] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    relationship: 'SPOUSE',
    isLegalGuardian: false,
    canViewRecords: false,
  });

  const [openAddProvider, setOpenAddProvider] = useState(false);
  const [newProvider, setNewProvider] = useState({
    name: '',
    role: '',
    credentials: '',
    organization: '',
    email: '',
    phone: '',
    isPrimary: false,
    specialties: '',
    availableForMessages: false,
    responseTime: '',
  });

  const canAddMoreFamily = familyMembers.length < 5;

  const handleAddFamilyMember = () => {
    if (!newMember.firstName || !newMember.lastName || !newMember.email) return;
    const member: FamilyMember = {
      id: `family-${Date.now()}`,
      ...newMember,
      addedAt: new Date().toISOString(),
    };
    setFamilyMembers([...familyMembers, member]);
    setOpenAddFamily(false);
    setNewMember({ firstName: '', lastName: '', email: '', phone: '', relationship: 'SPOUSE', isLegalGuardian: false, canViewRecords: false });
  };

  const handleRemoveFamilyMember = (id: string) => {
    setFamilyMembers(familyMembers.filter(m => m.id !== id));
  };

  const handleAddCareProvider = () => {
    if (!newProvider.name || !newProvider.role || !newProvider.email) return;
    const provider: CareProvider = {
      id: `provider-${Date.now()}`,
      ...newProvider,
      specialties: newProvider.specialties
        ? newProvider.specialties.split(',').map(s => s.trim()).filter(Boolean)
        : [],
      addedAt: new Date().toISOString(),
    };
    setCareTeam([...careTeam, provider]);
    setOpenAddProvider(false);
    setNewProvider({ name: '', role: '', credentials: '', organization: '', email: '', phone: '', isPrimary: false, specialties: '', availableForMessages: false, responseTime: '' });
  };

  const handleRemoveCareProvider = (id: string) => {
    setCareTeam(careTeam.filter(p => p.id !== id));
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Care Network
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Manage your family circle and healthcare providers in one place.
      </Typography>

      <Grid container spacing={3}>
        {/* My Family Group */}
        <Grid item xs={12}>
          <SectionCard
            title="My Family Group"
            subtitle={`${familyMembers.length} of 5 members added`}
            actions={
              <Button
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => setOpenAddFamily(true)}
                disabled={!canAddMoreFamily}
              >
                Add Member
              </Button>
            }
          >
            {familyMembers.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <FamilyRestroomIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary" gutterBottom>
                  No family members added yet
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setOpenAddFamily(true)}
                  sx={{ mt: 2 }}
                >
                  Add Your First Family Member
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {familyMembers.map((member) => (
                  <Grid item xs={12} sm={6} md={4} key={member.id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ bgcolor: 'info.main' }}>
                              <FamilyRestroomIcon />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {member.firstName} {member.lastName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {relationshipOptions.find(r => r.value === member.relationship)?.label || member.relationship}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton size="small" onClick={() => handleRemoveFamilyMember(member.id)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            <EmailIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                            {member.email}
                          </Typography>
                          {member.phone && (
                            <Typography variant="caption" color="text.secondary">
                              <PhoneIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                              {member.phone}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {member.isLegalGuardian && (
                            <Chip label="Legal Guardian" size="small" color="warning" />
                          )}
                          {member.canViewRecords && (
                            <Chip label="Can View Records" size="small" color="success" />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </SectionCard>
        </Grid>

        {/* My Care Team */}
        <Grid item xs={12}>
          <SectionCard
            title="My Care Team"
            subtitle="Your healthcare providers"
            actions={
              <Button
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => setOpenAddProvider(true)}
              >
                Add Provider
              </Button>
            }
          >
            {careTeam.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <LocalHospitalIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography color="text.secondary" gutterBottom>
                  No care providers added yet
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<PersonAddIcon />}
                  onClick={() => setOpenAddProvider(true)}
                  sx={{ mt: 2 }}
                >
                  Add Your First Care Provider
                </Button>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {careTeam.map((provider) => (
                  <Grid item xs={12} sm={6} md={4} key={provider.id}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                          <Avatar
                            sx={{
                              bgcolor: provider.isPrimary ? 'primary.main' : 'secondary.main',
                              width: 56,
                              height: 56,
                            }}
                          >
                            <LocalHospitalIcon />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {provider.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {provider.role}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {provider.credentials}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => handleRemoveCareProvider(provider.id)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        {provider.isPrimary && (
                          <Chip label="Primary Provider" size="small" color="primary" sx={{ mb: 1 }} />
                        )}

                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          {provider.organization}
                        </Typography>

                        {provider.specialties && provider.specialties.length > 0 && (
                          <Box sx={{ mb: 2 }}>
                            {provider.specialties.map((specialty, idx) => (
                              <Chip key={idx} label={specialty} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                            ))}
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {provider.availableForMessages && (
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<MessageIcon />}
                              component={RouterLink}
                              to="/patient/messages"
                              fullWidth
                            >
                              Send Message
                            </Button>
                          )}
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button size="small" variant="outlined" startIcon={<PhoneIcon />} href={`tel:${provider.phone}`} sx={{ flex: 1 }}>
                              Call
                            </Button>
                            <Button size="small" variant="outlined" startIcon={<EmailIcon />} href={`mailto:${provider.email}`} sx={{ flex: 1 }}>
                              Email
                            </Button>
                          </Box>
                        </Box>

                        {provider.responseTime && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'center' }}>
                            {provider.responseTime}
                          </Typography>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </SectionCard>
        </Grid>
      </Grid>

      {/* Add Family Member Dialog */}
      <Dialog open={openAddFamily} onClose={() => setOpenAddFamily(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Family Member</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="First Name" required value={newMember.firstName} onChange={(e) => setNewMember({ ...newMember, firstName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Last Name" required value={newMember.lastName} onChange={(e) => setNewMember({ ...newMember, lastName: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email" type="email" required value={newMember.email} onChange={(e) => setNewMember({ ...newMember, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Phone Number" type="tel" value={newMember.phone} onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Relationship</InputLabel>
                <Select value={newMember.relationship} label="Relationship" onChange={(e) => setNewMember({ ...newMember, relationship: e.target.value })}>
                  {relationshipOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={newMember.isLegalGuardian} onChange={(e) => setNewMember({ ...newMember, isLegalGuardian: e.target.checked })} />}
                label="This person is my legal guardian"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={newMember.canViewRecords} onChange={(e) => setNewMember({ ...newMember, canViewRecords: e.target.checked })} />}
                label="Allow access to my medical records"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddFamily(false)}>Cancel</Button>
          <Button onClick={handleAddFamilyMember} variant="contained" disabled={!newMember.firstName || !newMember.lastName || !newMember.email}>
            Add Member
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Care Provider Dialog */}
      <Dialog open={openAddProvider} onClose={() => setOpenAddProvider(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Care Provider</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Full Name" required value={newProvider.name} onChange={(e) => setNewProvider({ ...newProvider, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Role / Title" required value={newProvider.role} onChange={(e) => setNewProvider({ ...newProvider, role: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Credentials" placeholder="e.g. MD, RD, CDE" value={newProvider.credentials} onChange={(e) => setNewProvider({ ...newProvider, credentials: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Organization / Clinic" value={newProvider.organization} onChange={(e) => setNewProvider({ ...newProvider, organization: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Email" type="email" required value={newProvider.email} onChange={(e) => setNewProvider({ ...newProvider, email: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Phone Number" type="tel" value={newProvider.phone} onChange={(e) => setNewProvider({ ...newProvider, phone: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Specialties" placeholder="Comma-separated, e.g. Diabetes, CGM Integration" value={newProvider.specialties} onChange={(e) => setNewProvider({ ...newProvider, specialties: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Typical Response Time" placeholder="e.g. Usually within 24 hours" value={newProvider.responseTime} onChange={(e) => setNewProvider({ ...newProvider, responseTime: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={newProvider.isPrimary} onChange={(e) => setNewProvider({ ...newProvider, isPrimary: e.target.checked })} />}
                label="This is my primary provider"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={<Checkbox checked={newProvider.availableForMessages} onChange={(e) => setNewProvider({ ...newProvider, availableForMessages: e.target.checked })} />}
                label="Available for in-app messages"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddProvider(false)}>Cancel</Button>
          <Button onClick={handleAddCareProvider} variant="contained" disabled={!newProvider.name || !newProvider.role || !newProvider.email}>
            Add Provider
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
